(() => {
  'use strict';

  // ==== CONFIG ====
  const SELECTOR = '.cart-upsells-container'; // scope to upsells only

  // Throttled "run-last" repair scheduler
  let repairScheduled = false;
  const scheduleRepair = () => {
    if (repairScheduled) return;
    repairScheduled = true;
    // double rAF = after DOM replacement + any other inits
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try { repairUpsellScrollbar(); } finally { repairScheduled = false; }
      });
    });
  };

  // Normalize DOM so Swiper can measure/attach
  function normalizeMarkup(el) {
    el.classList.add('swiper'); // keep .swiper-container if present
    const wrapper = el.querySelector('.cart-upsells_products');
    if (wrapper) wrapper.classList.add('swiper-wrapper');

    el.querySelectorAll('.cart-upsell_product').forEach((s) => {
      s.classList.add('swiper-slide');
    });

    // Ensure scrollbar track exists
    let sb = el.querySelector('.swiper-scrollbar');
    if (!sb) {
      sb = document.createElement('div');
      sb.className = 'swiper-scrollbar';
      el.appendChild(sb);
    }
    return sb;
  }

  // Build Swiper params while preserving our scrollbar.el
  function buildParams(el, sb) {
    let opts = {};
    try { opts = JSON.parse(el.getAttribute('data-swiper-options') || '{}'); } catch (_) {}
    const defaults = {
      direction: 'horizontal',
      slidesPerView: 'auto',
      spaceBetween: 8,
      freeMode: { enabled: true },
      grabCursor: true,
      mousewheel: { forceToAxis: true },
      scrollbar: { el: sb, draggable: true, hide: false },
      observer: true,
      observeParents: true
    };
    return Object.assign({}, defaults, opts, {
      scrollbar: Object.assign({}, defaults.scrollbar, opts.scrollbar || {})
    });
  }

  // First-load initializer (keeps your last-working behavior)
  function initCartUpsellsSwiper(root = document) {
    // Guard: Swiper bundle must be present
    if (typeof window.Swiper === 'undefined') return;

    const containers = root.querySelectorAll(`${SELECTOR}.swiper`);
    containers.forEach((el) => {
      if (el.dataset.swiperInit === '1') return;

      const sb = el.querySelector('.swiper-scrollbar') || normalizeMarkup(el);
      const params = buildParams(el, sb);

      try {
        const inst = new Swiper(el, params);
        // settle sizes after images/layout
        requestAnimationFrame(() => {
          inst.update();
          inst.scrollbar && inst.scrollbar.updateSize && inst.scrollbar.updateSize();
        });
        el.dataset.swiperInit = '1';
      } catch (e) {
        // fail-safe: never throw in prod
        console.error('[upsells-swiper] init error', e);
      }
    });
  }

  // Repair after dynamic re-render:
  // - if track exists but no drag, or markup/classes were lost,
  //   rebuild cleanly so scrollbar attaches.
  function repairUpsellScrollbar(root = document) {
    if (typeof window.Swiper === 'undefined') return;

    root.querySelectorAll(SELECTOR).forEach((el) => {
      const track = el.querySelector('.swiper-scrollbar');
      const hasDrag = !!el.querySelector('.swiper-scrollbar .swiper-scrollbar-drag');

      // Nothing to repair if drag exists and instance is alive
      if (track && hasDrag && el.swiper && !el.swiper.destroyed) {
        // still ensure sizes in case layout changed
        el.swiper.update();
        el.swiper.scrollbar && el.swiper.scrollbar.updateSize && el.swiper.scrollbar.updateSize();
        return;
      }

      // Normalize markup & rebuild
      const sb = normalizeMarkup(el);
      if (el.swiper && !el.swiper.destroyed) {
        try { el.swiper.destroy(true, true); } catch (_) {}
      }

      const params = buildParams(el, sb);
      try {
        const inst = new Swiper(el, params);
        requestAnimationFrame(() => {
          inst.update();
          inst.scrollbar && inst.scrollbar.updateSize && inst.scrollbar.updateSize();
        });
        el.dataset.swiperInit = '1';
      } catch (e) {
        console.error('[upsells-swiper] repair error', e);
      }
    });
  }

  // Expose (optional) for manual debugging from console
  window.initCartUpsellsSwiper = (root) => initCartUpsellsSwiper(root || document);
  window.repairUpsellScrollbar = (root) => repairUpsellScrollbar(root || document);

  // ---- Event wiring (lightweight, idempotent) ----
  document.addEventListener('DOMContentLoaded', () => initCartUpsellsSwiper());
  document.addEventListener('shopify:section:load', (e) => initCartUpsellsSwiper(e.target));
  document.addEventListener('cart:updated', () => initCartUpsellsSwiper());

  // Fire this AFTER you finish replacing the cart HTML in your cart code.
  // (You already added these; keeping listener here is production-safe.)
  document.addEventListener('cart:rendered', () => scheduleRepair());

  // If legacy initializer runs (e.g., initializeSwiper), append our repair so we "win last".
  if (typeof window.initializeSwiper === 'function') {
    const orig = window.initializeSwiper;
    window.initializeSwiper = function patchedInitializeSwiper(...args) {
      const ret = orig.apply(this, args);
      scheduleRepair();
      return ret;
    };
  }
})();
